import React, { useRef, useState } from "react";
import axios from "axios";
import Turnstile, { TurnstileHandle } from "../account/Turnstile";
import { trackEvent } from "../analytics/tracking";

// Kept in step with the backend's list (Technogenious-BE- src/Quotes/quotes.service.ts).
export const PARISHES = ["Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester", "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny", "St. Ann", "St. Mary", "Portland", "St. Thomas"];
export const VENUE_TYPES = ["Bar", "Restaurant", "Night club", "Betting shop", "Hotel", "Other"];

/**
 * Leasing enquiry form on the Leasing page. Works without an account: the
 * backend runs the same bot checks as sign-up (hidden field, time on the form,
 * Turnstile when enabled) and saves the enquiry for staff.
 */
export default function LeaseEnquiry() {
  const openedAt = useRef(Date.now());
  const key = useRef("");
  const turnstile = useRef<TurnstileHandle>(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) || "").trim();
    setBusy(true);
    setError("");
    try {
      if (!key.current) key.current = crypto.randomUUID();
      const email = text("email");
      const message = text("message");
      const r = await axios.post(`${process.env.REACT_APP_SERVER_URL}/quotes/lease`, {
        requestKey: key.current,
        name: text("name"),
        phone: text("phone"),
        ...(email ? { email } : {}),
        parish: text("parish"),
        venueType: text("venueType"),
        machines: Number(text("machines")),
        ...(message ? { message } : {}),
        website: text("website"),
        formMs: Math.max(0, Math.round(Date.now() - openedAt.current)),
        ...(token ? { turnstileToken: token } : {}),
      });
      setReference(r.data.data.reference);
      trackEvent("generate_lead", { lead_type: "leasing" });
    } catch (e: any) {
      turnstile.current?.reset();
      const status = e?.response?.status;
      const detail = e?.response?.data?.message;
      setError(status === 429 ? "You have sent several enquiries. Please wait a little, or WhatsApp us." : status === 400 && typeof detail === "string" ? detail : status === 400 ? "Please check your details and try again." : "We couldn’t send your enquiry. Please try again, or WhatsApp us.");
    } finally {
      setBusy(false);
    }
  }

  if (reference)
    return <div className="lease-form-done" role="status"><h3>Thank you, we’ve received your enquiry.</h3><p>Your reference is <strong>{reference}</strong>. Our team will call you to talk about your location.</p></div>;

  return <form className="lease-form" onSubmit={submit} aria-labelledby="lease-form-title">
    <h3 id="lease-form-title">Send us your details</h3>
    <p className="lease-form-note">No account needed. We’ll call you back, usually within one business day.</p>
    <div className="lease-form-grid">
      <label>Your name<input name="name" required minLength={2} maxLength={100} autoComplete="name"/></label>
      <label>Phone or WhatsApp number<input name="phone" type="tel" required pattern="\+?[0-9 \(\)\-]{7,20}" maxLength={20} autoComplete="tel" placeholder="876 555 0123"/></label>
      <label>Email (optional)<input name="email" type="email" maxLength={254} autoComplete="email"/></label>
      <label>Parish<select name="parish" required defaultValue=""><option value="" disabled>Choose a parish</option>{PARISHES.map(p => <option key={p}>{p}</option>)}</select></label>
      <label>Type of venue<select name="venueType" required defaultValue=""><option value="" disabled>Choose a venue type</option>{VENUE_TYPES.map(v => <option key={v}>{v}</option>)}</select></label>
      <label>Number of machines<input name="machines" type="number" required min={1} max={50} defaultValue={2} inputMode="numeric"/></label>
    </div>
    <label>Anything else we should know? (optional)<textarea name="message" rows={3} maxLength={2000} placeholder="Opening hours, space available, best time to call…"/></label>
    <div className="lease-trap" aria-hidden="true"><label>Website<input name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue=""/></label></div>
    <Turnstile ref={turnstile} onToken={setToken}/>
    <button className="lease-button" type="submit" disabled={busy}>{busy ? "Sending…" : "Send enquiry"}</button>
    {error && <p role="alert" className="lease-form-error">{error}</p>}
  </form>;
}
